import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getSettings, patchSettings, listOperators,
  getAudit, listRateVersions, resetWorkspace,
  resetWorkspacePreview, resetWorkspaceReceipts,
} from "../lib/authApi";
import { retryViesPending, viesRetryRuns } from "../lib/api";
import { errorMessage } from "../lib/errorMessage";
import { useAuth } from "../auth/AuthContext";
import { Gear, ShieldCheck, Receipt, ClockCounterClockwise, SignOut, Warning } from "@phosphor-icons/react";
import { Section, Field, Input } from "../components/SettingsPrimitives";
import { RateVerifyPanel } from "../components/RateVerifyPanel";
import { RatePendingAcksPanel } from "../components/RatePendingAcksPanel";
import { ExchangeRatesTab } from "../components/ExchangeRatesTab";
import { VatRateOverridesPanel } from "../components/VatRateOverridesPanel";
import { RulesDownloadPanel } from "../components/RulesDownloadPanel";
import { ThemeToggle } from "../components/ThemeToggle";
import { toast } from "../components/ui/sonner";
import { labelFor } from "../lib/enumLabels";
import { OperatorsTab } from "../components/settings/OperatorsTab";

const BASE_TAB_KEYS = ["credentials", "operators", "exchange_rates", "rate_tables", "audit_log"];

export default function Settings() {
  const { t } = useTranslation();
  const { admin, signout, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isAdmin = (admin?.role || "").toUpperCase() === "ADMIN";
  const TAB_KEYS = isAdmin ? [...BASE_TAB_KEYS, "danger_zone"] : BASE_TAB_KEYS;
  // v0.6.10 — deep-linkable tabs. `?tab=rate_tables#rate-pending-acks`
  // is what the calc-level-gate CTA in the review queue points at; the
  // gate names Settings > Rate tables as the only place it clears, so
  // that destination has to be reachable in one click. Unknown or
  // not-permitted values fall back to the default tab rather than
  // rendering an empty page.
  const requestedTab = new URLSearchParams(loc.search).get("tab");
  const [tab, setTab] = useState(
    TAB_KEYS.includes(requestedTab) ? requestedTab : "credentials");
  const [settings, setSettings] = useState(null);
  const [operators, setOperators] = useState([]);
  const [audit, setAudit] = useState([]);
  const [rates, setRates] = useState({ EU_OSS: [], UK_VAT: [] });

  useEffect(() => {
    if (!loading && !admin) nav("/login");
    if (admin?.must_change_password) nav("/change-password");
  }, [admin, loading, nav]);

  useEffect(() => {
    if (!admin || admin.must_change_password) return;
    getSettings().then(setSettings).catch(() => {});
    listOperators().then(setOperators).catch(() => {});
    Promise.all([listRateVersions("EU_OSS"), listRateVersions("UK_VAT")])
      .then(([a, b]) => setRates({ EU_OSS: a, UK_VAT: b })).catch(() => {});
  }, [admin]);

  // v0.6.10 — scroll to the panel named in the URL hash once its tab has
  // rendered (the CTA deep-links to #rate-pending-acks, which sits well
  // below the fold of the Rate tables tab). Retried a few times because
  // the panel mounts after its own fetch resolves.
  useEffect(() => {
    const id = (loc.hash || "").replace(/^#/, "");
    if (!id || !settings) return undefined;
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        clearInterval(timer);
      } else if (++tries > 20) {
        clearInterval(timer);
      }
    }, 100);
    return () => clearInterval(timer);
  }, [loc.hash, tab, settings]);

  // Re-fetch audit when its tab is activated, and after any settings save.
  useEffect(() => {
    if (tab === "audit_log" && admin && !admin.must_change_password) {
      getAudit().then(setAudit).catch(() => {});
    }
  }, [tab, settings, operators, admin]);

  if (!admin || !settings) return <div className="p-6 text-slate-600 dark:text-slate-300">{t("ui.settings.loading")}</div>;

  return (
    <div data-testid="settings-page" className="min-h-screen bg-white dark:bg-slate-900">
      <header className="border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Gear size={20} className="text-teal-600 dark:text-teal-400" />
          <div className="text-sm tracking-normal text-slate-600 dark:text-slate-300">{t("ui.settings.title")}</div>
          <span className="text-xs text-slate-500 dark:text-slate-400">/</span>
          <span className="text-xs text-slate-500 dark:text-slate-400" data-testid="settings-admin-identity">{admin.display_name || (admin.email || "").split("@")[0]}</span>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <button data-testid="back-to-dashboard" onClick={() => nav("/")} className="text-xs text-slate-600 dark:text-slate-300 hover:text-teal-700 dark:text-teal-300 dark:hover:text-teal-300">{t("ui.settings.back_to_dashboard")}</button>
          <button data-testid="logout-button" onClick={() => { signout(); nav("/login"); }} className="text-xs text-slate-600 dark:text-slate-300 hover:text-red-700 dark:text-red-300 dark:hover:text-red-300 flex items-center gap-1">
            <SignOut size={12} /> {t("ui.settings.logout")}
          </button>
        </div>
      </header>

      <nav className="border-b border-slate-200 dark:border-slate-700 px-6 flex gap-px bg-slate-100 dark:bg-slate-800">
        {TAB_KEYS.map((key) => (
          <button
            key={key}
            data-testid={`tab-${key.replace(/_/g, '-')}`}
            onClick={() => setTab(key)}
            className={`text-xs px-4 py-3 rounded-xl ${
              tab === key
                ? "bg-white dark:bg-slate-900 text-teal-700 dark:text-teal-300 font-medium"
                : "bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:text-slate-200 dark:hover:text-slate-200"
            }`}
          >{t(`ui.settings.tabs.${key}`)}</button>
        ))}
      </nav>

      <main className="max-w-[1200px] mx-auto px-6 py-6">
        {tab === "credentials" && <CredentialsTab settings={settings} onSaved={setSettings} />}
        {tab === "operators" && <OperatorsTab operators={operators} setOperators={setOperators} />}
        {tab === "exchange_rates" && <ExchangeRatesTab isAdmin={admin?.role === "ADMIN"} />}
        {tab === "rate_tables" && <RatesTab rates={rates} isAdmin={admin?.role === "ADMIN"} settings={settings} onSaved={setSettings} />}
        {tab === "audit_log" && <AuditTab audit={audit} />}
        {tab === "danger_zone" && isAdmin && <DangerZoneTab />}
      </main>
    </div>
  );
}

function CredentialsTab({ settings, onSaved }) {
  const { t } = useTranslation();  // for any t() calls in this tab
  const { admin } = useAuth();
  const isAdmin = (admin?.role || "").toUpperCase() === "ADMIN";
  const [form, setForm] = useState({
    hmrc_client_id: settings.hmrc_client_id || "",
    hmrc_client_secret: "",
    hmrc_environment: settings.hmrc_environment || "sandbox",
    vies_base_url: settings.vies_base_url || "",
  });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true); setStatus(null);
    try {
      const next = await patchSettings(form);
      onSaved(next);
      setStatus({ ok: true, msg: "credentials valid ✓ saved" });
      setForm((p) => ({ ...p, hmrc_client_secret: "" }));
    } catch (e) {
      const d = e?.response?.data?.detail;
      setStatus({ ok: false, msg: d?.message || JSON.stringify(d) || e.message });
    } finally { setBusy(false); }
  };

  return (
    <>
      <Section titleKey="ui.settings.sections.hmrc_oauth" icon={<ShieldCheck size={14} />}>
        <Field labelKey="ui.settings.fields.client_id">
          <Input data-testid="hmrc-client-id" value={form.hmrc_client_id} onChange={(e) => setForm({ ...form, hmrc_client_id: e.target.value })} />
        </Field>
        <Field labelKey="ui.settings.fields.client_secret">
          <Input data-testid="hmrc-client-secret" type="password" placeholder={settings.hmrc_client_secret === "••••••" ? "•••••• (stored)" : ""} value={form.hmrc_client_secret} onChange={(e) => setForm({ ...form, hmrc_client_secret: e.target.value })} />
        </Field>
        <Field labelKey="ui.settings.fields.environment">
          <select data-testid="hmrc-environment" value={form.hmrc_environment} onChange={(e) => setForm({ ...form, hmrc_environment: e.target.value })} className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm rounded-xl text-slate-900 dark:text-slate-50">
            <option value="sandbox">{t("ui.settings.fields.env_sandbox", { defaultValue: "sandbox" })}</option>
            <option value="production">{t("ui.settings.fields.env_production", { defaultValue: "production" })}</option>
          </select>
        </Field>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {t("ui.settings.misc.sandbox_hint", { defaultValue: "Sandbox creds only validate against test-api.service.hmrc.gov.uk — production creds are rejected by sandbox and vice versa." })}
        </div>
        {status && (
          <div data-testid="creds-status" className={`text-xs ${status.ok ? "text-emerald-700 dark:text-emerald-300" : "text-red-700 dark:text-red-300"}`}>{status.msg}</div>
        )}
        <button data-testid="save-credentials" onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-700 disabled:opacity-40 text-white font-semibold px-5 py-2 text-xs rounded-xl">
          {busy ? t("ui.settings.misc.saving", { defaultValue: "Saving…" }) : t("ui.settings.misc.save_credentials", { defaultValue: "Save credentials" })}
        </button>
      </Section>
      <Section titleKey="ui.settings.sections.vies_endpoint" icon={<ShieldCheck size={14} />}>
        <Field labelKey="ui.settings.fields.base_url">
          <Input data-testid="vies-base-url" value={form.vies_base_url} onChange={(e) => setForm({ ...form, vies_base_url: e.target.value })} />
        </Field>
      </Section>
      {isAdmin && <ViesRetryPanel />}
    </>
  );
}


function ViesRetryPanel() {
  // v0.4.37 U2 — Manual VIES retry trigger.
  // Operator picks U2A=a (end of Credentials tab), U2B=a (spinner →
  // result chip), U2C=a (hide for OPERATOR — applied by parent),
  // U2D=a (no confirm dialog).
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const onRetry = async () => {
    setBusy(true); setResult(null);
    const r = await retryViesPending();
    setBusy(false);
    setResult(r);
  };
  const c = result?.counters || {};
  return (
    <Section
      titleKey="ui.settings.sections.vies_retry"
      icon={<ShieldCheck size={14} />}
    >
      <div className="text-xs text-slate-500 dark:text-slate-400">
        {t("ui.settings.misc.vies_retry_hint", { defaultValue: "Checks again, now, the customer VAT numbers that could not be checked. This also runs by itself once a day, up to 500 numbers at a time, on calculations not yet locked." })}
      </div>
      <button
        data-testid="vies-retry-button"
        onClick={onRetry}
        disabled={busy}
        className="bg-teal-600 hover:bg-teal-700 disabled:opacity-40 text-white font-semibold px-5 py-2 text-xs rounded-xl"
      >
        {busy ? "Retrying…" : t("ui.settings.misc.retry", { defaultValue: "Retry pending VIES checks" })}
      </button>
      {result && result.ok && (
        (() => {
          // v0.4.37 U4 nit fix — when nothing succeeded and errors
          // dominate, tint the chip amber so operators don't read it
          // as a green "success". Threshold: errors > (promoted +
          // reclassified + still_pending).
          const succeeded = (c.promoted || 0) + (c.reclassified || 0)
            + (c.still_pending || 0);
          const errCount = (result.errors || []).length;
          const tint = errCount > succeeded
            ? "text-amber-700 dark:text-amber-300"
            : "text-emerald-700 dark:text-emerald-300";
          return (
            <div
              data-testid="vies-retry-result"
              className={`text-xs ${tint} mt-2`}
            >
              {t("ui.settings.misc.retry_result", { promoted: c.promoted || 0, reclassified: c.reclassified || 0, pending: c.still_pending || 0, defaultValue: `${c.promoted || 0} promoted · ${c.reclassified || 0} reclassified · ${c.still_pending || 0} still pending` })}
              {(c.cooldown_skipped || 0) > 0 && (
                <span className="text-amber-700 dark:text-amber-300">{t("ui.settings.misc.retry_cooldown", { count: c.cooldown_skipped, defaultValue: ` · ${c.cooldown_skipped} cooldown-skipped` })}</span>
              )}
              {errCount > 0 && (
                <span className="text-rose-700 dark:text-rose-300">{t("ui.settings.misc.retry_errors", { count: errCount, defaultValue: ` · ${errCount} error(s)` })}</span>
              )}
            </div>
          );
        })()
      )}
      {result && !result.ok && (
        <div
          data-testid="vies-retry-error"
          className="text-xs text-rose-700 dark:text-rose-300 mt-2"
        >
          {t("ui.settings.misc.retry_error", { error: String(result.error), defaultValue: `error: ${String(result.error)}` })}
        </div>
      )}
    </Section>
  );
}


// The operator roster lives in components/settings/OperatorsTab (re-exported
// here so existing imports keep working).
export { OperatorsTab };

function RatesTab({ rates, isAdmin, settings, onSaved }) {
  const { t } = useTranslation();
  return (
    <Section titleKey="ui.settings.sections.rate_tables" icon={<Receipt size={14} />}>
      <div className="text-xs text-slate-500 dark:text-slate-400">
        {t("ui.settings.misc.rates_intro_before", { defaultValue: "Rates are repo-versioned (JSON under " })}<code>{t("ui.settings.messages.rates_path")}</code>{t("ui.settings.misc.rates_intro_after", { defaultValue: "). Use the flag below to notify whoever updates the repo when a jurisdiction changes." })}
      </div>
      {Object.entries(rates).map(([regime, versions]) => (
        <div key={regime} data-testid={`rate-regime-${regime}`} className="border border-slate-200 dark:border-slate-700 mt-3">
          <div className="bg-white dark:bg-slate-900 px-3 py-1 text-xs text-teal-700 dark:text-teal-300">{labelFor("regime", regime)}</div>
          {versions.map((v) => (
            <div key={v.version} className="grid grid-cols-12 gap-2 px-3 py-1.5 border-b border-slate-200 dark:border-slate-700 text-xs">
              <span className="col-span-3 text-slate-900 dark:text-slate-50">{v.version}</span>
              <span className="col-span-6 text-slate-600 dark:text-slate-300">{v.effective_from} → {v.effective_to}</span>
              <span className="col-span-3 text-slate-500 dark:text-slate-400 text-right">{t("ui.settings.misc.countries", { count: Object.keys(v.countries || {}).length, defaultValue: `${Object.keys(v.countries || {}).length} countries` })}</span>
            </div>
          ))}
        </div>
      ))}
      <VatRateOverridesPanel isAdmin={isAdmin} settings={settings} onSaved={onSaved} />
      <RateVerifyPanel isAdmin={isAdmin} />
      <RatePendingAcksPanel isAdmin={isAdmin} />
      <RulesDownloadPanel isAdmin={isAdmin} />
    </Section>
  );
}

function AuditTab({ audit }) {
  const { t } = useTranslation();
  return (
    <>
      <Section titleKey="ui.settings.sections.audit_log" icon={<ClockCounterClockwise size={14} />}>
        {audit.length === 0 ? (
          <div data-testid="audit-empty" className="text-xs text-slate-500 dark:text-slate-400">{t("ui.settings.messages.no_audit")}</div>
        ) : audit.map((a, i) => (
          <div key={i} data-testid={`audit-row-${i}`} className="grid grid-cols-12 gap-2 text-xs border-b border-slate-200 dark:border-slate-700 py-1">
            <span className="col-span-3 text-slate-500 dark:text-slate-400">{a.changed_at?.slice(0, 19)}</span>
            <span className="col-span-2 text-teal-700 dark:text-teal-300">{a.field}</span>
            <span className="col-span-3 text-slate-600 dark:text-slate-300 truncate">{String(a.old_value || "")} → {String(a.new_value || "")}</span>
            <span className="col-span-4 text-slate-500 dark:text-slate-400 truncate">{a.changed_by}</span>
          </div>
        ))}
      </Section>
      <ViesRetryStatsPanel />
    </>
  );
}


function ViesRetryStatsPanel() {
  // v0.4.37 U3 — Last 30 VIES batch retry runs. Plain table per
  // operator pick U3C=a (no sparkline lib). Empty state per U3D=a.
  const { t } = useTranslation();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await viesRetryRuns(30);
      if (!cancelled) {
        setRuns(r || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <Section
      titleKey="ui.settings.sections.vies_retry_stats"
      icon={<ClockCounterClockwise size={14} />}
    >
      {loading ? (
        <div data-testid="vies-retry-stats-loading" className="text-xs text-slate-500 dark:text-slate-400">
          {t("ui.settings.misc.loading", { defaultValue: "loading…" })}
        </div>
      ) : runs.length === 0 ? (
        <div data-testid="vies-retry-stats-empty" className="text-xs text-slate-500 dark:text-slate-400">
          {t("ui.settings.misc.retry_runs_empty", { defaultValue: "No retry runs yet — the worker runs daily; trigger a manual retry from the Credentials tab." })}
        </div>
      ) : (
        <div data-testid="vies-retry-stats-table" className="tabular-nums text-[11px]">
          <div className="grid grid-cols-12 gap-2 text-amber-700 dark:text-amber-300 tracking-normal border-b border-slate-200 dark:border-slate-700 pb-1">
            <span className="col-span-3">{t("ui.settings.misc.col_started", { defaultValue: "Started" })}</span>
            <span className="col-span-1 text-right">{t("ui.settings.misc.col_sel", { defaultValue: "Sel" })}</span>
            <span className="col-span-2 text-right">{t("ui.settings.misc.col_promoted", { defaultValue: "Promoted" })}</span>
            <span className="col-span-2 text-right">{t("ui.settings.misc.col_reclassified", { defaultValue: "Reclassified" })}</span>
            <span className="col-span-2 text-right">{t("ui.settings.misc.col_pending", { defaultValue: "Still pending" })}</span>
            <span className="col-span-2 text-right">{t("ui.settings.misc.col_errors", { defaultValue: "Errors / Cooldowns" })}</span>
          </div>
          {runs.map((r, i) => (
            <div
              key={i}
              data-testid={`vies-retry-stats-row-${i}`}
              className="grid grid-cols-12 gap-2 border-b border-slate-200 dark:border-slate-700 py-1 text-slate-500 dark:text-slate-400"
            >
              <span className="col-span-3 text-slate-600 dark:text-slate-300">{(r.started_at || "").slice(0, 19)}</span>
              <span className="col-span-1 text-right">{r.counters?.selected || 0}</span>
              <span className="col-span-2 text-right text-emerald-700 dark:text-emerald-300">{r.counters?.promoted || 0}</span>
              <span className="col-span-2 text-right text-amber-700 dark:text-amber-300">{r.counters?.reclassified || 0}</span>
              <span className="col-span-2 text-right">{r.counters?.still_pending || 0}</span>
              <span className="col-span-2 text-right text-slate-500 dark:text-slate-400">
                {(r.errors || []).length} / {(r.country_cooldowns_hit || []).length}
              </span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}



function DangerZoneTab() {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [error, setError] = useState(null);

  const refresh = React.useCallback(() => {
    resetWorkspacePreview().then(setPreview).catch(() => setPreview(null));
    resetWorkspaceReceipts().then(setReceipts).catch(() => setReceipts([]));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const run = async () => {
    setBusy(true); setError(null); setLastReceipt(null);
    try {
      const r = await resetWorkspace(confirm);
      setLastReceipt(r);
      setConfirm("");
      toast.success(`Workspace reset — ${r.total_removed} rows removed`);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(d?.message || JSON.stringify(d) || e.message);
    } finally { setBusy(false); }
  };

  const downloadReceipt = (rec) => {
    const blob = new Blob([JSON.stringify(rec, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reset-receipt-${(rec.executed_at || "").replace(/[:.]/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toDelete = preview?.to_delete || {};
  const nonZero = Object.entries(toDelete).filter(([, n]) => n > 0);
  const preserved = preview?.preserved || {};
  const canRun = confirm === "RESET" && !busy;

  return (
    <Section titleKey="ui.settings.tabs.danger_zone" icon={<Warning size={14} />}>
      <div data-testid="danger-zone-tab" className="border border-red-300 dark:border-red-800 rounded-xl p-4 bg-red-50/60 dark:bg-red-950/30">
        <div className="flex items-center gap-2 text-red-700 dark:text-red-300 text-sm font-semibold">
          <Warning size={16} /> {t("ui.settings.reset.title", { defaultValue: "Reset workspace" })}
        </div>
        <p className="text-xs text-slate-600 dark:text-slate-300 mt-2">
          {t("ui.settings.reset.explainer", { defaultValue: "Permanently deletes all working/filing data below. Configuration (entities incl. golden clients, settings, admins, rate history, VIES cache) is preserved. This cannot be undone — a receipt is recorded." })}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div data-testid="reset-preview-delete" className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-900">
            <div className="text-xs font-semibold text-red-700 dark:text-red-300 mb-2">
              {t("ui.settings.reset.will_delete", { defaultValue: "Will delete" })}{" "}<span data-testid="reset-total-to-remove">{preview?.total_to_remove ?? "…"}</span> {t("ui.settings.reset.rows", { defaultValue: "rows" })}
            </div>
            {nonZero.length === 0 ? (
              <div data-testid="reset-nothing-to-delete" className="text-xs text-slate-500 dark:text-slate-400">{t("ui.settings.reset.nothing_to_delete", { defaultValue: "Nothing to delete — workspace already clean." })}</div>
            ) : (
              <div className="tabular-nums text-[11px] space-y-1">
                {nonZero.map(([name, n]) => (
                  <div key={name} data-testid={`reset-del-${name}`} className="flex justify-between border-b border-slate-100 dark:border-slate-800 py-0.5">
                    <span className="text-slate-600 dark:text-slate-300">{name}</span>
                    <span className="text-red-700 dark:text-red-300">{n}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div data-testid="reset-preview-preserved" className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-900">
            <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-2">{t("ui.settings.reset.preserved", { defaultValue: "Preserved (never deleted)" })}</div>
            <div className="tabular-nums text-[11px] space-y-1">
              {Object.entries(preserved).map(([name, n]) => (
                <div key={name} data-testid={`reset-keep-${name}`} className="flex justify-between border-b border-slate-100 dark:border-slate-800 py-0.5">
                  <span className="text-slate-600 dark:text-slate-300">{name}</span>
                  <span className="text-emerald-700 dark:text-emerald-300">{n}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2">
          <label className="text-xs text-slate-600 dark:text-slate-300">{t("ui.settings.reset.type_before", { defaultValue: "Type" })} <code className="text-red-700 dark:text-red-300">RESET</code> {t("ui.settings.reset.type_after", { defaultValue: "to confirm" })}</label>
          <input
            data-testid="reset-confirm-input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="RESET"
            className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 px-3 py-2 text-xs rounded-xl text-slate-900 dark:text-slate-50 w-40"
          />
          <button
            data-testid="reset-workspace-button"
            onClick={run}
            disabled={!canRun}
            className="bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-5 py-2 text-xs rounded-xl"
          >
            {busy ? t("ui.settings.reset.resetting", { defaultValue: "Resetting…" }) : t("ui.settings.reset.run", { defaultValue: "Reset workspace" })}
          </button>
        </div>
        {error && <div data-testid="reset-error" className="text-xs text-rose-700 dark:text-rose-300 mt-2">{error}</div>}
        {lastReceipt && (
          <div data-testid="reset-last-receipt" className="text-xs text-emerald-700 dark:text-emerald-300 mt-3 flex items-center gap-3">
            <span>{t("ui.settings.reset.last_receipt", { n: lastReceipt.total_removed, at: (lastReceipt.executed_at || "").slice(0, 19), defaultValue: `Removed ${lastReceipt.total_removed} rows at ${(lastReceipt.executed_at || "").slice(0, 19)}.` })}</span>
            <button data-testid="download-last-receipt" onClick={() => downloadReceipt(lastReceipt)} className="underline text-teal-700 dark:text-teal-300">{t("ui.settings.reset.download_receipt", { defaultValue: "Download receipt" })}</button>
          </div>
        )}
      </div>

      <div data-testid="reset-receipts-history" className="mt-6">
        <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">{t("ui.settings.reset.history", { defaultValue: "Reset history" })}</div>
        {receipts.length === 0 ? (
          <div data-testid="reset-receipts-empty" className="text-xs text-slate-500 dark:text-slate-400">{t("ui.settings.reset.no_history", { defaultValue: "No resets recorded yet." })}</div>
        ) : (
          <div className="text-[11px]">
            <div className="grid grid-cols-12 gap-2 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 pb-1">
              <span className="col-span-4">{t("ui.settings.reset.col_executed", { defaultValue: "Executed" })}</span>
              <span className="col-span-5">{t("ui.settings.reset.col_by", { defaultValue: "By" })}</span>
              <span className="col-span-2 text-right">{t("ui.settings.reset.col_rows", { defaultValue: "Rows" })}</span>
              <span className="col-span-1 text-right"> </span>
            </div>
            {receipts.map((r, i) => (
              <div key={i} data-testid={`reset-receipt-row-${i}`} className="grid grid-cols-12 gap-2 border-b border-slate-100 dark:border-slate-800 py-1 items-center">
                <span className="col-span-4 text-slate-600 dark:text-slate-300">{(r.executed_at || "").slice(0, 19)}</span>
                <span className="col-span-5 text-slate-600 dark:text-slate-300 truncate">{r.executed_by}</span>
                <span className="col-span-2 text-right text-red-700 dark:text-red-300">{r.total_removed}</span>
                <button data-testid={`download-receipt-${i}`} onClick={() => downloadReceipt(r)} className="col-span-1 text-right underline text-teal-700 dark:text-teal-300">⤓</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}
